import { useState, type FormEvent } from 'react';
import { useSession } from '../App';
import type { Device } from '../api';
import { Link } from '../router';
import { Card, ConfirmDialog, ErrorNotice, Loading, Notice, PageHeader, useAsync } from '../components/ui';
import { Rings } from '../components/Rings';
import { PLATFORM_LABELS, formatDate, formatRelative } from '../format';

export function DevicesPage() {
  const { client } = useSession();
  const state = useAsync(() => client.devices(), [client]);
  const [editing, setEditing] = useState<{ id: string; name: string } | null>(null);
  const [removing, setRemoving] = useState<Device | null>(null);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<unknown>(null);

  if (state.loading && !state.data) return <Loading />;
  if (state.error) return <ErrorNotice error={state.error} onRetry={() => void state.reload()} />;
  const list = state.data!;
  const atLimit = list.limit !== null && list.used >= list.limit;

  async function rename(event: FormEvent) {
    event.preventDefault();
    if (!editing || !editing.name.trim()) return;
    setBusy(true);
    setActionError(null);
    try {
      await client.renameDevice(editing.id, editing.name.trim());
      setEditing(null);
      await state.reload();
    } catch (error) {
      setActionError(error);
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    if (!removing) return;
    setBusy(true);
    setActionError(null);
    try {
      await client.removeDevice(removing.id);
      setRemoving(null);
      await state.reload();
    } catch (error) {
      setActionError(error);
      setRemoving(null);
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <PageHeader
        eyebrow="Devices"
        title="Your devices"
        description="Every installation of DeskTouch signed in to your account."
        actions={
          <span className="counter">
            {list.used} of {list.limit ?? 'unlimited'} in use
          </span>
        }
      />

      {atLimit && (
        <Notice tone="warning">
          You have reached your plan's device limit. Remove a device you no longer use, or{' '}
          <Link to="/billing">move to a plan with more devices</Link>.
        </Notice>
      )}
      <ErrorNotice error={actionError} />

      <Card>
        {list.devices.length === 0 ? (
          <div className="empty small">
            <Rings state="searching" size={72} />
            <p>No devices yet.</p>
            <p className="muted">Install DeskTouch and sign in with this account — it will show up here.</p>
          </div>
        ) : (
          <ul className="device-list">
            {list.devices.map((device) => (
              <li key={device.id}>
                <span className={`platform ${device.platform}`} aria-hidden="true">
                  {(PLATFORM_LABELS[device.platform] ?? device.platform).slice(0, 3)}
                </span>
                <div className="device-main">
                  {editing?.id === device.id ? (
                    <form className="inline-form" onSubmit={rename}>
                      <input
                        autoFocus
                        value={editing.name}
                        maxLength={120}
                        aria-label="Device name"
                        onChange={(event) => setEditing({ id: device.id, name: event.target.value })}
                      />
                      <button type="submit" className="primary small" disabled={busy || !editing.name.trim()}>
                        Save
                      </button>
                      <button type="button" className="ghost small" onClick={() => setEditing(null)}>
                        Cancel
                      </button>
                    </form>
                  ) : (
                    <strong>{device.name}</strong>
                  )}
                  <span className="fine">
                    {PLATFORM_LABELS[device.platform] ?? device.platform}
                    {device.appVersion ? ` · DeskTouch ${device.appVersion}` : ''}
                    {device.connectedToName ? ` · connected to ${device.connectedToName}` : ''}
                  </span>
                  <span className="fine">
                    Active {formatRelative(device.lastSeenAt)} · added {formatDate(device.createdAt)}
                  </span>
                </div>
                {editing?.id !== device.id && (
                  <div className="row-actions">
                    <button type="button" className="ghost small" onClick={() => setEditing({ id: device.id, name: device.name })}>
                      Rename
                    </button>
                    <button type="button" className="ghost small danger-text" onClick={() => setRemoving(device)}>
                      Remove
                    </button>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </Card>

      <ConfirmDialog
        open={removing !== null}
        title={`Remove ${removing?.name ?? 'this device'}?`}
        confirmLabel="Remove device"
        tone="danger"
        busy={busy}
        onCancel={() => setRemoving(null)}
        onConfirm={() => void remove()}
      >
        <p>
          The app on that device is signed out of your account straight away and frees up a slot. Its local data stays
          on the device. You can add it again by signing in there.
        </p>
      </ConfirmDialog>
    </>
  );
}
